import React, { useState } from 'react';
import { parseISO } from 'date-fns';
import ConfirmDialog from './ConfirmDialog';
import { formatDate } from '../../../helpers/dates';
import { dayKey } from '../../../helpers/desk-booking';
import type { ConfirmDialogOptions } from './ConfirmDialog';
import type { BookingIndex } from '../../../helpers/desk-booking';
import type { Desk, DeskBlock } from '../../../types';

type Props = {
  desks: Array<Desk>,
  date: string,
  index: BookingIndex,
  checkedDeskIds: Array<number>,
  busy: boolean,
  onCheckedChange: (deskIds: Array<number>) => void,
  onBlock: (deskIds: Array<number>, reason: string) => void,
  onUnblock: (blocks: Array<DeskBlock>) => void,
  onDone: () => void,
};

const joinLabels = (desks: Array<Desk>) => desks.map((desk) => desk.label).join(', ');

// Admin toolbar above the map for blocking (or unblocking) several desks on the shown day at once.
// Desks are checked on the map; checked free/booked desks get blocked, checked blocked desks get unblocked.
const BlockDesksBar = (props: Props): React.ReactElement => {
  const {
    desks, date, index, checkedDeskIds, busy, onCheckedChange, onBlock, onUnblock, onDone,
  } = props;
  const [dialog, setDialog] = useState<ConfirmDialogOptions | null>(null);
  const shortDate = formatDate(parseISO(date), 'EEE dd.MM.');

  const blockOf = (desk: Desk) => index.blocksByDeskDate[dayKey(desk.id, date)];
  const checked = desks.filter((desk) => checkedDeskIds.includes(desk.id));
  const toBlock = checked.filter((desk) => !blockOf(desk));
  const toUnblock = checked.filter((desk) => blockOf(desk));
  const blockable = desks.filter((desk) => desk.active && !blockOf(desk));
  const allBlockableChecked = blockable.length > 0 && blockable.every((desk) => checkedDeskIds.includes(desk.id));

  const handleBlock = () => {
    const bookings = toBlock.map((desk) => index.byDeskDate[dayKey(desk.id, date)]).filter(Boolean);
    const many = toBlock.length > 1;
    setDialog({
      title: many ? `Block ${toBlock.length} desks on ${shortDate}?` : `Block ${toBlock[0].label} on ${shortDate}?`,
      message: (
        <>
          Nobody will be able to book <strong>{joinLabels(toBlock)}</strong> that day.
          {bookings.length > 0 && (
            <>
              {' '}Booking{bookings.length === 1 ? '' : 's'} of{' '}
              <strong>{bookings.map((booking) => booking.userName).join(', ')}</strong> will be cancelled.
            </>
          )}
        </>
      ),
      confirmLabel: many ? `Block ${toBlock.length} desks` : 'Block desk',
      danger: bookings.length > 0,
      input: { label: 'Reason', defaultValue: 'Workshop', maxLength: 100 },
      onConfirm: (reason) => onBlock(toBlock.map((desk) => desk.id), reason),
    });
  };

  const handleUnblock = () => {
    setDialog({
      title: `Unblock ${toUnblock.length > 1 ? `${toUnblock.length} desks` : toUnblock[0].label} on ${shortDate}?`,
      message: <>After unblocking, anyone can book <strong>{joinLabels(toUnblock)}</strong> that day.</>,
      confirmLabel: 'Unblock',
      onConfirm: () => onUnblock(toUnblock.map(blockOf)),
    });
  };

  return (
    <div className="desk-block-bar">
      <div className="desk-block-bar__text">
        <strong>Block desks on {shortDate}</strong>
        <small>
          {checked.length
            ? `${checked.length} selected: ${joinLabels(checked)}`
            : 'Click desks on the map to select them. Select blocked desks to unblock them.'}
        </small>
      </div>
      <div className="desk-block-bar__actions">
        <button
          type="button"
          className="btn btn-sm btn-link"
          disabled={!blockable.length}
          onClick={() => onCheckedChange(allBlockableChecked ? [] : blockable.map((desk) => desk.id))}
        >
          {allBlockableChecked ? 'Clear' : 'Select all'}
        </button>
        {toUnblock.length > 0 && (
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={handleUnblock}>
            Unblock {toUnblock.length}
          </button>
        )}
        <button type="button" className="btn btn-sm btn-primary" disabled={busy || !toBlock.length} onClick={handleBlock}>
          Block{toBlock.length ? ` ${toBlock.length}` : ''}
        </button>
        <button type="button" className="btn btn-sm btn-light" onClick={onDone}>Done</button>
      </div>

      {dialog && <ConfirmDialog {...dialog} onClose={() => setDialog(null)} />}
    </div>
  );
};

export default BlockDesksBar;
